import React, { useState } from "react";
import { useNavigate } from "../router";

const FAQ_ITEMS = [
  {
    question: "What is Trustd.ai Guest Screening and what does it cover?",
    answer: [
      <>
        Trustd.ai is an advanced risk-management platform specifically designed
        for the <strong>Short-Term Rental (STR)</strong> industry. Our screening
        goes beyond traditional checks by combining:
      </>,
      <ul className="faq-answer-list">
        <li>
          <strong>Identity Verification:</strong> Ensuring guests are who they
          say they are.
        </li>
        <li>
          <strong>Background Screening:</strong> Checking major criminal
          databases, sex offender registries, and global sanctions lists.
        </li>
        <li>
          <strong>Past Stay History:</strong> Analyzing a guest's historical stay
          footprints across multiple booking platforms to predict potential risks
          like property damage or unauthorized parties. Please note that this
          feature will be released in due course, once the platform has
          accumulated sufficient data volume.
        </li>
      </ul>,
    ],
  },
  {
    question: "How is ID Verification conducted?",
    answer: [
      <>
        ID Verification compares the information on the guest's uploaded ID with
        the reservation details provided during the booking process. Specifically,
        we extract the <strong>Name</strong> and{" "}
        <strong>Date of Birth (DOB)</strong> from the provided identification and
        compare it directly against the data provided at the time of booking. This
        ensures the person staying at your property matches the person who made
        the reservation.
      </>,
      <>
        Please note that ID Verification is designed to validate the consistency
        of the guest's identity information. As with any identity verification
        process, it does not independently determine whether an ID document was
        obtained fraudulently or altered outside of the verification process.
      </>,
    ],
  },
  {
    question: "How does credit usage work for Guest Screening and Selfie Verification?",
    answer: [
      <>
        Our standard Guest Screening service includes ID Verification and
        Background Screening and is charged at <strong>1 credit per screening</strong>.
        Selfie Verification is an optional additional layer of identity
        verification that can be added when you want greater confidence that the
        person completing the screening matches the identity document provided.
      </>,
      <>
        If Selfie Verification is not selected, the guest screening remains at
        <strong> 1 credit per screening</strong>. If Selfie Verification is added,
        1 additional credit is charged, bringing the total to <strong>2 credits
        per guest</strong> for Background Screening, ID Verification, and Selfie
        Verification.
      </>,
      <>
        This gives you the flexibility to use the standard screening at 1 credit
        or add enhanced selfie-based verification when it is needed.
      </>,
    ],
  },
  {
    question: "How is Background Screening performed?",
    answer: [
      <>We conduct thorough checks against major criminal databases, including:</>,
      <ul className="faq-answer-list">
        <li>Multi-state criminal records.</li>
        <li>National Sex Offender registries.</li>
        <li>
          Global Sanctions, Watchlists, Criminal Databases, and more.
        </li>
      </ul>,
      <>
        This helps identify high-risk individuals before they step foot on your
        property.
      </>,
    ],
  },
  {
    question: "What happens if I want to cancel a reservation based on a screening result?",
    answer: [
      <>
        Trustd.ai provides the data to help you make informed decisions, but we
        do not cancel reservations on your behalf. If you choose to cancel, we
        highly suggest following these steps:
      </>,
      <>
        <strong>Step 1 - contact us first:</strong> Please contact us by clicking
        the <strong>Contact Support</strong> button in your Trustd.ai Host Portal
        or by emailing <strong>support@trustd.ai</strong>. When reaching out,
        please include the unique guest report ID and a brief description of your
        request. We will provide you with a guest-facing version of the screening
        report.
      </>,
      <>
        <strong>Step 2 - inform guests:</strong> Notify your guest about your
        decision and reasoning. Please do not share the report available in your
        portal directly with your guests. It should be treated as private and
        confidential information. Share the guest-facing report we provide in
        Step 1.
      </>,
      <>
        <strong>Step 3 - follow your cancellation policy:</strong> Your PMS
        likely has a cancellation policy configured for your booking channels,
        such as Airbnb, Vrbo, and others. We recommend following the cancellation
        policy associated with your reservation.
      </>,
    ],
  },
  {
    question: "What makes Trustd.ai different from other screening companies?",
    answer: [
      <>
        While most companies rely solely on criminal background checks, we
        believe those records are often insufficient or irrelevant to the actual
        risks of hosting.
      </>,
      <>
        Our difference is our focus on <strong>stay history</strong>. We
        prioritize a guest's past stays across different booking platforms. This
        data provides much greater peace of mind than a traditional background
        check alone, as it tracks how a guest treats a property.
      </>,
    ],
  },
  {
    question: "When will the \"stay history\" metric be fully available?",
    answer: [
      <>
        We are currently in the data-collection phase, continuously gathering
        insights from integrated <strong>Property Management Systems (PMS)</strong>.
        Because the <strong>Returning Guest Ratio</strong> in the STR sector is
        naturally lower than in other industries, it takes more time for this
        behavioral metric to mature. We are committed to delivering this
        high-value metric as soon as we reach the necessary data volume to ensure
        its accuracy.
      </>,
    ],
  },
  {
    question: "Is the Trustd.ai solution booking-channel independent?",
    answer: [
      <>
        <strong>Yes.</strong> We capture data through your PMS from all
        integrated channels. Whether your guest books through Airbnb, Vrbo,
        Booking.com, or your own direct booking site, our system captures and
        screens the data regardless of the source.
      </>,
    ],
  },
  {
    question: "What is the difference between a SOFT NAME MATCH and a POTENTIAL NAME MATCH in a background check, and what action should I take in each case?",
    answer: [
      <>
        Guest background checks return three possible outcomes: <strong>CLEAR</strong>,
        <strong> SOFT NAME MATCH</strong>, and <strong>POTENTIAL NAME MATCH</strong>.
      </>,
      <>
        A <strong>CLEAR</strong> outcome means that no record matching the guest's
        name and date of birth was identified in the databases connected to our
        systems. No action is needed on your part.
      </>,
      <>
        A <strong>SOFT NAME MATCH</strong> outcome, by itself, does <strong>not
        constitute evidence of a criminal conviction</strong>. It indicates that
        the guest's name and date of birth match an individual associated with a
        criminal court record; however, the available record does not contain a
        confirmed conviction outcome. The absence of a conviction may reflect a
        pending case, dismissal, acquittal, or incomplete or unavailable court
        data.
      </>,
      <>
        <strong>Action on your part:</strong> Hosts are advised to exercise their
        independent judgment and discretion when evaluating a Soft Name Match.
        Some hosts continue hosting when there is a Soft Name Match. If you elect
        to cancel the reservation based on information contained in the report,
        please contact us by opening a support ticket and requesting a
        <strong> “Guest-Facing Report”</strong> for the applicable record. We will
        prepare the guest-facing version of the report and provide it to you for
        delivery to the guest.
      </>,
      <>
        A <strong>POTENTIAL NAME MATCH</strong> outcome means that a guest's name
        and date of birth match a record appearing on a recognized government or
        law-enforcement database or watchlist, including OFAC sanctions lists,
        United Nations sanctions lists, or the FBI Wanted list; or that the
        guest's name and date of birth match an individual associated with a
        criminal record reflecting a confirmed conviction.
      </>,
      <>
        <strong>Action on your part:</strong> Hosts are advised to exercise their
        independent judgment and discretion when evaluating a Potential Name
        Match. If you elect to cancel the reservation based on information
        contained in the report, please contact us by opening a support ticket
        and requesting a <strong>“Guest-Facing Report”</strong> for the applicable
        record. We will prepare the guest-facing version of the report and
        provide it to you for delivery to the guest.
      </>,
    ],
  },
  {
    question: "Can I configure different screening requirements for different properties?",
    answer: [
      <>
        <strong>Yes.</strong> Hosts can configure screening requirements at the
        property level, giving you greater flexibility when managing multiple
        properties. Whether you manage just a few properties or hundreds of
        properties, you can assign different screening levels to different
        properties or property groups.
      </>,
      <>
        For example, you can use Standard Screening (Background Check + ID
        Verification) for one group of properties, while requiring Selfie
        Verification in addition to Standard Screening for another group. This
        allows you to tailor your guest screening requirements based on the
        needs, risk level, or policies of each property.
      </>,
    ],
  },
  {
    question: "Can I fully automate my guest screening process?",
    answer: [
      <>
        <strong>Yes.</strong> You can achieve full automation by integrating
        Trustd.ai with your PMS. By using your PMS messaging templates and
        triggers, you can automatically send screening links to guests and
        receive results without manual intervention.
      </>,
    ],
  },
  {
    question: "Why might I need a second review for a screening match?",
    answer: [
      <>
        Potential identity matches can appear in public records without indicating a
        criminal conviction or a verified identity match to the guest. Public databases
        often vary in structure, formatting, and the level of available
        information, which can make automated name matching less reliable in
        certain cases.
      </>,
      <>
        To help minimize false positives and avoid unnecessary concerns or
        friction for both hosts and guests, Trustd.ai uses an additional review
        step for potential matches. This allows us to validate the details
        more carefully before presenting a result, ensuring that hosts receive
        more accurate and meaningful screening information rather than being
        alerted based on an unrelated record.
      </>,
    ],
  },
  {
    question: "Do I need to use a PMS (Property Management System) to use Trustd.ai?",
    answer: [
      <>
        <strong>No.</strong> While we offer deep integrations with many PMS
        platforms, you can also sign up and use our services on a self-serve
        basis through our portal. This allows you to manually input guest data
        and receive screening reports whenever you need them.
      </>,
    ],
  },
  {
    question: "Is there a minimum number of properties required to use the system?",
    answer: [
      <>
        <strong>No.</strong> Our system is designed to scale with your business.
        Whether you manage a single property or a portfolio of 1,000+ properties,
        you have full access to our entire suite of screening tools.
      </>,
    ],
  },
];

export default function FAQ() {
  const [activeFaq, setActiveFaq] = useState(0);
  const navigate = useNavigate();

  return (
    <div className="faq-page-modern">
      <section className="faq-shell-modern">
        <div className="faq-hero-card-modern">
          <p className="dashboard-section-kicker">Support</p>
          <h2>Frequently Asked Questions</h2>
          <p className="dashboard-section-note">
            Everything you need to know about Trustd.ai Guest Screening.
          </p>
        </div>

        <div className="faq-list-card-modern">
          {FAQ_ITEMS.map((item, index) => {
            const isActive = activeFaq === index;
            return (
              <article key={item.question} className={`faq-item-modern ${isActive ? "active" : ""}`}>
                <button
                  type="button"
                  className="faq-item-toggle-modern"
                  onClick={() => setActiveFaq(isActive ? -1 : index)}
                  aria-expanded={isActive}
                >
                  <span className="faq-question-wrap">
                    <span className="faq-number">{index + 1}</span>
                    <span>{item.question}</span>
                  </span>
                  <i className={`pi ${isActive ? "pi-chevron-up" : "pi-chevron-down"}`} aria-hidden="true" />
                </button>
                {isActive ? (
                  <div className="faq-item-content-modern">
                    {item.answer.map((paragraph, paragraphIndex) => (
                      React.isValidElement(paragraph) && paragraph.type === "ul" ? (
                        React.cloneElement(paragraph, { key: paragraphIndex })
                      ) : (
                        <p key={paragraphIndex}>{paragraph}</p>
                      )
                    ))}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>

      </section>

      <p className="faq-contact-note">
        <strong>Didn't find the answer you were looking for?</strong>{" "}
        <button type="button" onClick={() => navigate("/contact")}>
          Contact us!
        </button>
      </p>
    </div>
  );
}
